import { useLayoutEffect, useRef, useState } from "react";

export const CLOUD_CARD_WIDTH = 1280;
export const CLOUD_SINGLE_MAX_WIDTH = 1440;
export const CLOUD_GRID_GAP = 8;

export function fitCloudGrid(count, width, height, cardHeight) {
  // Three hosts share a full-height row; four use two rows on desktop.
  const preferredColumns = count === 3 && width >= 900 ? 3 : count === 4 && width >= 600 ? 2 : null;
  const fillCells = count === 3 || count === 4;
  const baseWidth = preferredColumns === 3 ? 960 : CLOUD_CARD_WIDTH;
  let best = { columns: 1, rows: 1, scale: 0, score: -Infinity };
  for (let columns = 1; columns <= Math.max(1, Math.min(count, 12)); columns += 1) {
    if (preferredColumns && columns !== preferredColumns) continue;
    const rows = Math.max(1, Math.ceil(count / columns));
    const cellWidth = Math.min(count === 1 ? CLOUD_SINGLE_MAX_WIDTH : Infinity, Math.max(1, (width - CLOUD_GRID_GAP * (columns - 1)) / columns));
    const cellHeight = Math.max(1, (height - CLOUD_GRID_GAP * (rows - 1)) / rows);
    const scale = Math.min(1.3, cellWidth / baseWidth, cellHeight / Math.max(1, cardHeight));
    const score = scale - (columns * rows - count) * 0.002;
    if (score > best.score) best = {
      columns, rows, scale, score,
      cardWidth: fillCells ? cellWidth / scale : CLOUD_CARD_WIDTH,
      fillHeight: fillCells ? cellHeight / scale : null,
    };
  }
  return best;
}

export default function useCloudFit(count, cardHeight) {
  const pageRef = useRef(null), gridRef = useRef(null);
  const [size, setSize] = useState({ pageHeight: null, width: CLOUD_CARD_WIDTH, height: 900 });
  useLayoutEffect(() => {
    let frame;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!pageRef.current) return;
        const pageHeight = Math.max(1, window.innerHeight - pageRef.current.getBoundingClientRect().top - 8);
        const next = { pageHeight, width: gridRef.current?.clientWidth || 1, height: gridRef.current?.clientHeight || 1 };
        setSize(current => Object.keys(next).every(key => Math.abs((current[key] || 0) - next[key]) < 0.5) ? current : next);
      });
    };
    const observer = new ResizeObserver(measure);
    if (pageRef.current) observer.observe(pageRef.current);
    if (pageRef.current?.parentElement) observer.observe(pageRef.current.parentElement);
    if (gridRef.current) observer.observe(gridRef.current);
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    measure();
    return () => {
      observer.disconnect(); cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, [count]);
  return { pageRef, gridRef, pageHeight: size.pageHeight, ...fitCloudGrid(count, size.width, size.height, cardHeight) };
}
